import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import { AdminPermitDetail, PermitStateChange, PermitHistoryEntry, PopulatedUser, PopulatedStatus } from '../../models/permit.model';
import { PermitStatusService } from '../../services/permit-status.service';
import { PermitStatus } from '../../models/permit-status.model';
import { I18nString } from '../../models/permit-category.model';
import { PermitHistoryAction } from '../../constants/permit.constants';

// Actor labels written by automated integrations (not human users). Kept explicit so we
// can flag periodic external syncs in the timeline.
const EXTERNAL_SYNC_ACTORS = ['PSD Sync'];
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

// A single point on the timeline, whichever feed (status change or generic activity) it
// came from — this is what lets both feeds share one chronological, day-grouped list.
interface UnifiedEntry {
    id: string;
    kind: 'status' | 'activity';
    date: Date;
    icon: string;
    isSync: boolean;
    statusLabel?: string;
    statusColor?: string;
    reason?: string;
    conditions?: string;
    actionTitle?: string;
    detail?: string;
    actor: string;
    time: string;
}

interface HistoryDayGroup {
    dateKey: string;
    dayLabel: string;
    entries: UnifiedEntry[];
}

@Component({
    selector: 'app-permit-history',
    templateUrl: './permit-history.component.html',
    styleUrls: ['./permit-history.component.scss'],
})
export class PermitHistoryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() permit: AdminPermitDetail;

    dayGroups: HistoryDayGroup[] = [];

    // Periodic external syncs are collapsed into a single "last synced" indicator so they
    // don't bloat the timeline and bury real events.
    lastSyncedAt: string | null = null;

    // stateHistory[].status often arrives as a raw id (unlike permit.state which is populated),
    // so resolve names/colors from the status definitions.
    private statusMap: Record<string, PermitStatus> = {};

    private langSub: Subscription;
    private destroy$ = new Subject<void>();

    constructor(
        private statusService: PermitStatusService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.loadStatuses();
        this.buildDayGroups();
        this.langSub = this.translate.onLangChange.subscribe((_: LangChangeEvent) => this.buildDayGroups());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit) {
            this.buildDayGroups();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.langSub?.unsubscribe();
    }

    private loadStatuses(): void {
        this.statusService
            .getAllStatuses(true)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (statuses) => {
                    this.statusMap = {};
                    (statuses || []).forEach((s) => {
                        this.statusMap[s._id] = s;
                    });
                    // Status names/colors weren't resolvable until now — rebuild with them.
                    this.buildDayGroups();
                },
                error: () => {
                    this.statusMap = {};
                },
            });
    }

    private getTime(dateStr: string): number {
        const time = new Date(dateStr).getTime();
        return isNaN(time) ? 0 : time;
    }

    // An invalid/missing date must still sort predictably (last, oldest-first) rather than
    // produce NaN comparisons — coerce it to the epoch instead of leaving it Invalid Date.
    private safeDate(dateStr: string): Date {
        const time = new Date(dateStr).getTime();
        return isNaN(time) ? new Date(0) : new Date(time);
    }

    private buildDayGroups(): void {
        const stateHistory = this.permit?.stateHistory || [];
        const history = this.permit?.history || [];

        // Status changes: drop periodic no-op syncs (a sync entry whose status matches the
        // preceding entry), but keep genuine transitions — even if pushed by an external system.
        const ascending = [...stateHistory].sort((a, b) => this.getTime(a.changedAt) - this.getTime(b.changedAt));
        const keptStateChanges: PermitStateChange[] = [];
        let prevStatusId: string | null = null;
        for (const entry of ascending) {
            const statusId = this.statusId(entry.status);
            const isNoOpSync = this.isExternalSync(entry.changedBy) && statusId === prevStatusId;
            // No-op syncs are collapsed into the "last synced" indicator; everything else is kept.
            if (!isNoOpSync) {
                keptStateChanges.push(entry);
                prevStatusId = statusId;
            }
        }

        // Activity: drop sync heartbeats (pure PSD_SYNC + no-op status-change syncs).
        const keptActivity = history.filter((h) => !this.isSyncHeartbeat(h));

        const statusEntries: UnifiedEntry[] = keptStateChanges.map((e) => ({
            id: `status-${e.changedAt}-${this.statusId(e.status)}`,
            kind: 'status',
            date: this.safeDate(e.changedAt),
            icon: this.isExternalSync(e.changedBy) ? 'sync' : 'adjust',
            isSync: this.isExternalSync(e.changedBy),
            statusLabel: this.statusLabel(e.status),
            statusColor: this.statusColor(e.status),
            reason: e.reason,
            conditions: e.conditions,
            actor: this.getActorName(e.changedBy),
            time: this.formatTime(e.changedAt),
        }));

        const activityEntries: UnifiedEntry[] = keptActivity.map((h) => ({
            id: h._id,
            kind: 'activity',
            date: this.safeDate(h.timestamp),
            icon: this.isExternalSync(h.actor) ? 'sync' : 'info',
            isSync: this.isExternalSync(h.actor),
            actionTitle: this.translate.instant('permit.manage.history.action_' + h.action),
            detail: h.description,
            actor: this.getActorName(h.actor),
            time: this.formatTime(h.timestamp),
        }));

        const all = [...statusEntries, ...activityEntries].sort((a, b) => b.date.getTime() - a.date.getTime());
        this.dayGroups = this.groupByDay(all);

        // Latest sync time across both feeds → single "last synced" indicator.
        const syncDates = [
            ...stateHistory.filter((e) => this.isExternalSync(e.changedBy)).map((e) => e.changedAt),
            ...history.filter((h) => this.isExternalSync(h.actor)).map((h) => h.timestamp),
        ].filter(Boolean);
        this.lastSyncedAt = syncDates.length ? syncDates.reduce((latest, d) => (this.getTime(d) > this.getTime(latest) ? d : latest)) : null;
    }

    private statusId(status: PopulatedStatus | string): string {
        if (!status) return '';
        return typeof status === 'string' ? status : status._id;
    }

    private isSyncHeartbeat(entry: PermitHistoryEntry): boolean {
        if (!this.isExternalSync(entry.actor)) return false;
        if (entry.action === PermitHistoryAction.PSD_SYNC) return true;
        // A status-change sync that didn't actually change the status is noise.
        return entry.action === PermitHistoryAction.STATUS_CHANGE && !!entry.metadata && entry.metadata.fromStatus === entry.metadata.toStatus;
    }

    // ─── Day grouping ────────────────────────────────────────────────────────

    private dateKeyOf(d: Date): string {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    private groupByDay(entries: UnifiedEntry[]): HistoryDayGroup[] {
        const groupMap = new Map<string, HistoryDayGroup>();
        const order: string[] = [];
        for (const entry of entries) {
            const dateKey = this.dateKeyOf(entry.date);
            let group = groupMap.get(dateKey);
            if (!group) {
                group = { dateKey, dayLabel: this.getDayLabel(dateKey), entries: [] };
                groupMap.set(dateKey, group);
                order.push(dateKey);
            }
            group.entries.push(entry);
        }
        return order.map((k) => groupMap.get(k)).filter((group): group is HistoryDayGroup => !!group);
    }

    private getDayLabel(dateKey: string): string {
        const today = new Date();
        const todayKey = this.dateKeyOf(today);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yesterdayKey = this.dateKeyOf(yesterday);

        const [y, m, d] = dateKey.split('-').map(Number);
        const locale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        const formatted = new Date(y, m - 1, d).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });

        if (dateKey === todayKey) return `${this.translate.instant('permit.manage.history.today')} · ${formatted}`;
        if (dateKey === yesterdayKey) return `${this.translate.instant('permit.manage.history.yesterday')} · ${formatted}`;
        return formatted;
    }

    private formatTime(dateStr: string): string {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const locale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        const hour12 = this.translate.currentLang !== 'fr';
        return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12 });
    }

    // ─── Status resolution ─────────────────────────────────────────────────────

    private resolveStatus(status: PopulatedStatus | string): PopulatedStatus | PermitStatus | null {
        if (!status) return null;
        if (typeof status === 'string') return this.statusMap[status] || null;
        if ((status as PopulatedStatus).name) return status;
        return this.statusMap[(status as PopulatedStatus)._id] || null;
    }

    statusLabel(status: PopulatedStatus | string): string {
        const name = this.resolveStatus(status)?.name as I18nString | string | undefined;
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    statusColor(status: PopulatedStatus | string): string {
        return this.resolveStatus(status)?.color || '#9ca3af';
    }

    // ─── Actor / sync helpers ──────────────────────────────────────────────────

    isExternalSync(actor: string | PopulatedUser): boolean {
        return typeof actor === 'string' && EXTERNAL_SYNC_ACTORS.includes(actor);
    }

    getActorName(actor: string | PopulatedUser): string {
        if (!actor) return '—';
        if (typeof actor !== 'string') {
            const user = actor as PopulatedUser;
            return `${user.firstName} ${user.lastName}`.trim() || '—';
        }
        const label = actor.trim();
        // A bare ObjectId is an unpopulated user we can't name here; system labels (e.g. "PSD Sync") are shown as-is.
        if (!label || OBJECT_ID_RE.test(label)) return '—';
        return label;
    }
}
